import { db } from './codexDB';
import { SEED_TS } from '@/lib/constants';
import { ATLAS_NODES, ATLAS_PLANET, atlasPublicPath } from '@/features/worlds/atlas/atlasNodes';
import type { Entity, Link } from '@/types/entity';

/**
 * 공간 아틀라스 시드 — 노드 엔티티(신규 location 생성) + meta.atlas + 계층 링크 + 컷 이미지를 **단일 트랜잭션 원자 적재**.
 * seedCities의 비차단 패턴과 달리 entities/links를 만지므로 loadOrSeed에서 **hydrate 전 await**(부분실패·store 미반영 방지).
 * 멱등: atlas 자산(seed-atlas-*) 있으면 이미지 skip. 링크는 from/to/type 존재 시 skip(기존 located_in 보존). 같은 노드 옛 seed city는 정리(단독 소유).
 */

const ATLAS_VERSION = 1;
const atlasAssetId = (nodeId: string) => `seed-atlas-v${ATLAS_VERSION}-${nodeId}`;
const atlasLinkId = (nodeId: string) => `seed-atlas-link-${nodeId}`;

export async function seedAtlas(): Promise<void> {
  if (typeof fetch === 'undefined') return; // jsdom 등 skip
  try {
    // 1) 이미지 Blob 선확보(트랜잭션 밖). 멱등: atlas 자산 있으면 skip. stale(옛 seed city) 수집.
    const puts: { id: string; entityId: string; blob: Blob; staleIds: string[] }[] = [];
    for (const n of ATLAS_NODES) {
      const aid = atlasAssetId(n.nodeId);
      if (await db.assets.get(aid)) continue;
      const res = await fetch(atlasPublicPath(n.publicKey));
      if (!res.ok) continue;
      const blob = await res.blob();
      if (!blob.size) continue;
      const staleIds = (await db.assets
        .where('entityId')
        .equals(n.nodeId)
        .filter((a) => a.kind === 'city' && a.id.startsWith('seed-') && a.id !== aid)
        .primaryKeys()) as string[];
      puts.push({ id: aid, entityId: n.nodeId, blob, staleIds });
    }

    // 2) 원자 트랜잭션: 엔티티(신규 생성+meta) + 계층 링크 + 자산
    await db.transaction('rw', db.entities, db.links, db.assets, async () => {
      let i = 0;
      for (const n of ATLAS_NODES) {
        let e = await db.entities.get(n.nodeId);
        if (!e && n.create) {
          // 신규 location — canon climb 위해 fields.parent=행성(계층은 parent_child 링크로 별도)
          e = {
            id: n.nodeId,
            type: 'location',
            name: n.create.name,
            summary: n.create.kind,
            fields: { parent: ATLAS_PLANET, kind: n.create.kind, desc: n.create.desc },
            meta: { atlas: n.meta },
            canonStatus: 'canon',
            confidence: 'confirmed',
            version: 1,
            createdAt: SEED_TS + i,
            updatedAt: SEED_TS + i,
          };
          await db.entities.put(e);
        } else if (e) {
          await db.entities.update(n.nodeId, { meta: { ...(e.meta ?? {}), atlas: n.meta } });
        }
        // 계층 링크(멱등 — 기존 located_in 보존)
        if (n.parentId && e) {
          const type: Link['type'] = n.parentId === ATLAS_PLANET ? 'located_in' : 'parent_child';
          const exists = await db.links.where('from').equals(n.nodeId).filter((l) => !l.deletedAt && l.to === n.parentId && l.type === type).count();
          if (!exists) {
            const link: Link = { id: atlasLinkId(n.nodeId), from: n.nodeId, to: n.parentId, type, directed: true, canonStatus: 'canon', confidence: 'confirmed', version: 1 };
            await db.links.put(link);
          }
        }
        i++;
      }
      for (let j = 0; j < puts.length; j++) {
        const p = puts[j];
        if (p.staleIds.length) await db.assets.bulkDelete(p.staleIds);
        await db.assets.put({ id: p.id, entityId: p.entityId, kind: 'city', blob: p.blob, mime: p.blob.type || 'image/png', name: 'atlas', createdAt: SEED_TS + 200 + j });
      }
    });
  } catch {
    /* 오프라인/테스트 — 다음 부팅 재시도 */
  }
}
